import { useEffect, useState } from "react";
import { packagesApi, paymentsApi, messageFrom } from "../api";

// Purchase report bundles via Bank of Georgia.
// Flow: pick package -> server creates a BOG order -> we redirect to BOG's
// hosted payment page -> BOG calls our callback -> limit is credited.
function BuyLimits({ onPurchased }) {
    const [packages, setPackages] = useState([]);
    const [orders, setOrders] = useState([]);
    const [busyId, setBusyId] = useState("");
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");

    const loadOrders = () => {
        paymentsApi.orders().then((res) => setOrders(res.data.data || [])).catch(() => {});
    };

    useEffect(() => {
        packagesApi.list().then((res) => setPackages(res.data.data || [])).catch(() => {});
        loadOrders();
    }, []);

    // Handle the redirect back from BOG (?payment=success&order=...).
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const payment = params.get("payment");
        const order = params.get("order");
        if (!payment) return;

        window.history.replaceState({}, "", window.location.pathname);

        if (payment === "fail") {
            setError("Payment was not completed.");
            return;
        }
        if (payment === "success" && order) {
            setNotice("Confirming your payment…");
            paymentsApi
                .verify(order)
                .then((res) => {
                    const status = res.data.data.status;
                    if (status === "completed") {
                        setNotice("Payment confirmed — your reports have been added.");
                        onPurchased && onPurchased();
                        loadOrders();
                    } else {
                        setNotice("Payment is still processing. Your balance updates automatically once it clears.");
                    }
                })
                .catch(() => setError("Could not confirm the payment. If you were charged, it will be credited shortly."));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const buy = (pkg) => {
        setError("");
        setNotice("");
        setBusyId(pkg.id);
        paymentsApi
            .checkout(pkg.id)
            .then((res) => {
                const url = res.data.data.redirect_url;
                if (url) window.location.href = url;
                else setError("Payment provider did not return a redirect URL");
            })
            .catch((err) => setError(messageFrom(err, "Could not start payment")))
            .finally(() => setBusyId(""));
    };

    return (
        <div className="panel">
            <div className="panel__head">
                <h3>Buy reports</h3>
                <p>Add reports to your balance. Payments are processed securely by Bank of Georgia.</p>
            </div>

            {error && <div className="banner banner--error">{error}</div>}
            {notice && <div className="banner banner--ok">{notice}</div>}

            <div className="buy-grid">
                {packages.map((p) => (
                    <div key={p.id} className={`buy-card${p.popular ? " buy-card--popular" : ""}`}>
                        {p.popular && <span className="buy-card__badge">Best value</span>}
                        <div className="buy-card__reports">{p.reports.toLocaleString()}</div>
                        <div className="buy-card__label">reports</div>
                        <div className="buy-card__price">${p.price.toLocaleString()}</div>
                        <div className="buy-card__per">${p.perReport.toFixed(2)} / report</div>
                        <button className="btn btn--primary btn--block" onClick={() => buy(p)} disabled={busyId === p.id}>
                            {busyId === p.id ? "Redirecting…" : "Buy"}
                        </button>
                    </div>
                ))}
            </div>

            {orders.length > 0 && (
                <div className="orders">
                    <h4>Purchase history</h4>
                    <table className="orders__table">
                        <thead>
                            <tr><th>Date</th><th>Package</th><th>Reports</th><th>Amount</th><th>Status</th></tr>
                        </thead>
                        <tbody>
                            {orders.map((o) => (
                                <tr key={o.external_order_id}>
                                    <td>{new Date(o.created_at).toLocaleDateString()}</td>
                                    <td>{o.package_id}</td>
                                    <td>{Number(o.reports).toLocaleString()}</td>
                                    <td>{Number(o.amount).toLocaleString()} {o.currency}</td>
                                    <td><span className={`pill pill--${o.status}`}>{o.status}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default BuyLimits;
